import React, { useRef, useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { upload_image_table } from "../../App/actions/layerActions";
import { set_value_properties } from "../../App/actions/propertiesActions";
import icon_upload from "../../Assets/svg/icon_upload.svg";
import uuid from "../../App/validation/uuid";
import dict from "../../Data/dict.json";

// Zoom step and bounds
const ZOOM_STEP = 0.25;
const ZOOM_MIN = 1;
const ZOOM_MAX = 10;

// Helper to calculate distance between two touches
const getDistance = (touches) =>
  Math.hypot(
    touches[0].clientX - touches[1].clientX,
    touches[0].clientY - touches[1].clientY,
  );

// Helper to calculate the true rendered size of the image within the container (accounting for objectFit: "contain")
const getRenderedSize = (imgElement) => {
  const { naturalWidth, naturalHeight, offsetWidth, offsetHeight } = imgElement;
  if (!naturalWidth || !naturalHeight)
    return { renderedWidth: offsetWidth, renderedHeight: offsetHeight };

  const imgRatio = naturalWidth / naturalHeight;
  const containerRatio = offsetWidth / offsetHeight;
  let renderedWidth, renderedHeight;

  if (imgRatio > containerRatio) {
    renderedWidth = offsetWidth;
    renderedHeight = offsetWidth / imgRatio;
  } else {
    renderedHeight = offsetHeight;
    renderedWidth = offsetHeight * imgRatio;
  }
  return { renderedWidth, renderedHeight };
};

// Helper to calculate bounded offsets to prevent panning beyond the canvas/viewport borders
const calculateBoundedOffset = (
  targetX,
  targetY,
  currentScale,
  imgElement,
  viewportElement,
) => {
  if (!imgElement || !viewportElement || currentScale <= 1)
    return { x: 0, y: 0 };

  const { renderedWidth, renderedHeight } = getRenderedSize(imgElement);
  const viewportWidth = viewportElement.offsetWidth;
  const viewportHeight = viewportElement.offsetHeight;

  const scaledWidth = renderedWidth * currentScale;
  const scaledHeight = renderedHeight * currentScale;

  let maxX = 0;
  let maxY = 0;

  // Calculate the max allowed offset if the scaled image is larger than the viewport
  if (scaledWidth > viewportWidth) {
    maxX = (scaledWidth - viewportWidth) / 2;
  }
  if (scaledHeight > viewportHeight) {
    maxY = (scaledHeight - viewportHeight) / 2;
  }

  // Clamp the target offsets
  return {
    x: Math.min(Math.max(targetX, -maxX), maxX),
    y: Math.min(Math.max(targetY, -maxY), maxY),
  };
};

export default function C_E_IMG({
  row,
  field,
  onClose,
  language = "id",
  fields_image,
}) {
  const dispatch = useDispatch();
  const fileInput = useRef(null);
  const viewportRef = useRef(null);
  const imgRef = useRef(null);

  // ── Main View Zoom / Pan State ──────────────────────────────────────────────
  const [scale, set_scale] = useState(1);
  const [offset, set_offset] = useState({ x: 0, y: 0 });

  // ── Smooth Loading & Height State ───────────────────────────────────────────
  const [isImgLoading, setIsImgLoading] = useState(true);
  const [currentHeight, setCurrentHeight] = useState("200px");
  const [knownHeight, setKnownHeight] = useState(null);

  // ── Full Screen State ───────────────────────────────────────────────────────
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fsScale, setFsScale] = useState(1);
  const [fsOffset, setFsOffset] = useState({ x: 0, y: 0 });
  const fsContainerRef = useRef(null);
  const fsImgRef = useRef(null);

  // Sync state to refs for use inside native event listeners
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);
  const fsScaleRef = useRef(fsScale);
  const fsOffsetRef = useRef(fsOffset);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    fsScaleRef.current = fsScale;
  }, [fsScale]);

  useEffect(() => {
    fsOffsetRef.current = fsOffset;
  }, [fsOffset]);

  const layer_state = useSelector((state) => state.layer);
  const properties_state = useSelector((state) => state.properties);

  const layer_id = layer_state?.layer_id;
  const feature_key_nested_table = properties_state?.feature_key_nested_table;

  const imageSrc = row?.properties?.[field?.key] || "";
  const mode_map = properties_state.mode_map;

  // ── Navigation State ────────────────────────────────────────────────────────
  const currentIndex =
    fields_image?.findIndex((f) => f.key === field?.key) ?? -1;
  const totalImages = fields_image?.length || 0;
  const hasNav = totalImages > 1;

  // Reset zoom & pan whenever the image changes
  useEffect(() => {
    set_scale(1);
    set_offset({ x: 0, y: 0 });
    setFsScale(1);
    setFsOffset({ x: 0, y: 0 });
    setIsImgLoading(true);
  }, [imageSrc]);

  // Constrain limits reactively if zoom level changes (keeps bounds sticky if zoomed out slowly)
  useEffect(() => {
    set_offset((prev) =>
      calculateBoundedOffset(
        prev.x,
        prev.y,
        scale,
        imgRef.current,
        viewportRef.current,
      ),
    );
  }, [scale]);

  useEffect(() => {
    setFsOffset((prev) =>
      calculateBoundedOffset(
        prev.x,
        prev.y,
        fsScale,
        fsImgRef.current,
        fsContainerRef.current,
      ),
    );
  }, [fsScale]);

  // ── Navigation Handlers ───────────────────────────────────────────────────
  const handlePrev = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (currentIndex >= 0) {
        const prevIndex =
          currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
        const prevField = fields_image[prevIndex];
        dispatch(
          set_value_properties({
            key: "global_image_editor",
            value: { row, field: prevField, fields_image },
          }),
        );
      }
    },
    [currentIndex, totalImages, fields_image, dispatch, row],
  );

  const handleNext = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      if (currentIndex >= 0) {
        const nextIndex =
          currentIndex === totalImages - 1 ? 0 : currentIndex + 1;
        const nextField = fields_image[nextIndex];
        dispatch(
          set_value_properties({
            key: "global_image_editor",
            value: { row, field: nextField, fields_image },
          }),
        );
      }
    },
    [currentIndex, totalImages, fields_image, dispatch, row],
  );

  const navRefs = useRef({ handleNext, handlePrev, hasNav });
  useEffect(() => {
    navRefs.current = { handleNext, handlePrev, hasNav };
  }, [handleNext, handlePrev, hasNav]);

  // ── Native Listeners (Inline Viewer) ────────────────────────────────────────
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    let isDragging = false;
    let lastMouse = null;
    let lastTouch = null;
    let pinchStartDist = null;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    let wheelAccumulatorX = 0;
    let lastWheelTime = 0;
    let wheelSwipeCooldown = false;
    let wheelTimeout = null;

    const handleWheel = (e) => {
      e.preventDefault();
      if (e.ctrlKey) {
        // Trackpad pinch zoom
        const delta = e.deltaY * -0.01;
        set_scale((s) => Math.min(Math.max(s + delta, ZOOM_MIN), ZOOM_MAX));
      } else if (scaleRef.current > 1) {
        // Trackpad/Mouse pan (incorporate bounding check)
        set_offset((prev) =>
          calculateBoundedOffset(
            prev.x - e.deltaX,
            prev.y - e.deltaY,
            scaleRef.current,
            imgRef.current,
            viewport,
          ),
        );
      } else {
        // Scale is 1: Handle trackpad swipe or mouse wheel zoom
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          // Trackpad swipe navigation
          const now = Date.now();
          if (now - lastWheelTime > 150) wheelAccumulatorX = 0;
          lastWheelTime = now;

          if (!wheelSwipeCooldown && navRefs.current.hasNav) {
            wheelAccumulatorX += e.deltaX;
            const swipeThreshold = 100;

            if (wheelAccumulatorX > swipeThreshold) {
              navRefs.current.handleNext();
              wheelSwipeCooldown = true;
              wheelAccumulatorX = 0;
              wheelTimeout = setTimeout(() => {
                wheelSwipeCooldown = false;
              }, 500);
            } else if (wheelAccumulatorX < -swipeThreshold) {
              navRefs.current.handlePrev();
              wheelSwipeCooldown = true;
              wheelAccumulatorX = 0;
              wheelTimeout = setTimeout(() => {
                wheelSwipeCooldown = false;
              }, 500);
            }
          }
        } else {
          // Standard Mouse wheel zoom
          const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
          set_scale((s) => Math.min(Math.max(s + delta, ZOOM_MIN), ZOOM_MAX));
        }
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchStartDist = getDistance(e.touches);
      } else if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;

        if (scaleRef.current > 1) {
          lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStartDist) {
        e.preventDefault();
        const currentDist = getDistance(e.touches);
        const deltaDist = currentDist - pinchStartDist;
        const pinchSensitivity = 0.01;

        set_scale((prev) =>
          Math.min(
            Math.max(prev + deltaDist * pinchSensitivity, ZOOM_MIN),
            ZOOM_MAX,
          ),
        );
        pinchStartDist = currentDist;
      } else if (e.touches.length === 1) {
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;

        if (scaleRef.current > 1 && lastTouch) {
          e.preventDefault();
          const dx = touchEndX - lastTouch.x;
          const dy = touchEndY - lastTouch.y;

          set_offset((prev) =>
            calculateBoundedOffset(
              prev.x + dx,
              prev.y + dy,
              scaleRef.current,
              imgRef.current,
              viewport,
            ),
          );
          lastTouch = { x: touchEndX, y: touchEndY };
        } else if (scaleRef.current <= 1) {
          const dx = Math.abs(touchStartX - touchEndX);
          const dy = Math.abs(touchStartY - touchEndY);
          if (dx > dy && dx > 10) {
            e.preventDefault();
          }
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStartDist = null;
      if (e.touches.length === 0) {
        lastTouch = null;

        if (scaleRef.current <= 1 && navRefs.current.hasNav) {
          const deltaX = touchStartX - touchEndX;
          const deltaY = touchStartY - touchEndY;
          const swipeThreshold = 50;

          if (
            Math.abs(deltaX) > swipeThreshold &&
            Math.abs(deltaX) > Math.abs(deltaY)
          ) {
            if (deltaX > 0) {
              navRefs.current.handleNext();
            } else {
              navRefs.current.handlePrev();
            }
          }
        }
      }
    };

    const handleMouseDown = (e) => {
      if (scaleRef.current <= 1 || e.button !== 0) return;
      e.preventDefault();
      isDragging = true;
      lastMouse = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e) => {
      if (!isDragging || scaleRef.current <= 1) return;
      e.preventDefault();
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;

      set_offset((prev) =>
        calculateBoundedOffset(
          prev.x + dx,
          prev.y + dy,
          scaleRef.current,
          imgRef.current,
          viewport,
        ),
      );
      lastMouse = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging = false;
      lastMouse = null;
    };

    viewport.addEventListener("wheel", handleWheel, { passive: false });
    viewport.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    viewport.addEventListener("touchmove", handleTouchMove, { passive: false });
    viewport.addEventListener("touchend", handleTouchEnd);
    viewport.addEventListener("touchcancel", handleTouchEnd);
    viewport.addEventListener("mousedown", handleMouseDown);

    window.addEventListener("mousemove", handleMouseMove, { passive: false });
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      viewport.removeEventListener("wheel", handleWheel);
      viewport.removeEventListener("touchstart", handleTouchStart);
      viewport.removeEventListener("touchmove", handleTouchMove);
      viewport.removeEventListener("touchend", handleTouchEnd);
      viewport.removeEventListener("touchcancel", handleTouchEnd);
      viewport.removeEventListener("mousedown", handleMouseDown);

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      if (wheelTimeout) clearTimeout(wheelTimeout);
    };
  }, []);

  const handleImageLoad = (e) => {
    setIsImgLoading(false);
    const height = e.target.getBoundingClientRect().height;
    setCurrentHeight(`${height}px`);
    if (!knownHeight) {
      setKnownHeight(height);
    }
  };

  const zoom_in = () =>
    set_scale((s) => Math.min(+(s + ZOOM_STEP).toFixed(2), ZOOM_MAX));
  const zoom_out = () =>
    set_scale((s) => Math.max(+(s - ZOOM_STEP).toFixed(2), ZOOM_MIN));
  const zoom_reset = () => {
    set_scale(1);
    set_offset({ x: 0, y: 0 });
  };

  // ── Full Screen Management ────────────────────────────────────────────────
  useEffect(() => {
    if (isFullScreen) {
      document.body.style.overflow = "hidden";
      setFsScale(1);
      setFsOffset({ x: 0, y: 0 });
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isFullScreen]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isFullScreen) return;
      if (e.key === "Escape") setIsFullScreen(false);
      if (e.key === "ArrowRight" && hasNav) handleNext();
      if (e.key === "ArrowLeft" && hasNav) handlePrev();
    },
    [isFullScreen, hasNav, handleNext, handlePrev],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Full Screen Native Listeners
  useEffect(() => {
    const fsContainer = fsContainerRef.current;
    if (!fsContainer || !isFullScreen) return;

    let isDragging = false;
    let lastMouse = null;
    let lastTouch = null;
    let pinchStartDist = null;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchEndX = 0;
    let touchEndY = 0;

    let wheelAccumulatorX = 0;
    let lastWheelTime = 0;
    let wheelSwipeCooldown = false;
    let wheelTimeout = null;

    const handleFsWheel = (e) => {
      e.preventDefault();
      if (e.ctrlKey) {
        const delta = e.deltaY * -0.01;
        setFsScale((s) => Math.min(Math.max(s + delta, ZOOM_MIN), ZOOM_MAX));
      } else if (fsScaleRef.current > 1) {
        setFsOffset((prev) =>
          calculateBoundedOffset(
            prev.x - e.deltaX,
            prev.y - e.deltaY,
            fsScaleRef.current,
            fsImgRef.current,
            fsContainer,
          ),
        );
      } else {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          const now = Date.now();
          if (now - lastWheelTime > 150) wheelAccumulatorX = 0;
          lastWheelTime = now;

          if (!wheelSwipeCooldown && navRefs.current.hasNav) {
            wheelAccumulatorX += e.deltaX;
            const swipeThreshold = 100;

            if (wheelAccumulatorX > swipeThreshold) {
              navRefs.current.handleNext();
              wheelSwipeCooldown = true;
              wheelAccumulatorX = 0;
              wheelTimeout = setTimeout(() => {
                wheelSwipeCooldown = false;
              }, 500);
            } else if (wheelAccumulatorX < -swipeThreshold) {
              navRefs.current.handlePrev();
              wheelSwipeCooldown = true;
              wheelAccumulatorX = 0;
              wheelTimeout = setTimeout(() => {
                wheelSwipeCooldown = false;
              }, 500);
            }
          }
        } else {
          const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
          setFsScale((s) => Math.min(Math.max(s + delta, ZOOM_MIN), ZOOM_MAX));
        }
      }
    };

    const handleFsTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        pinchStartDist = getDistance(e.touches);
      } else if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchEndX = touchStartX;
        touchEndY = touchStartY;

        if (fsScaleRef.current > 1) {
          lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
      }
    };

    const handleFsTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStartDist) {
        e.preventDefault();
        const currentDist = getDistance(e.touches);
        const deltaDist = currentDist - pinchStartDist;
        const pinchSensitivity = 0.01;

        setFsScale((prev) =>
          Math.min(
            Math.max(prev + deltaDist * pinchSensitivity, ZOOM_MIN),
            ZOOM_MAX,
          ),
        );
        pinchStartDist = currentDist;
      } else if (e.touches.length === 1) {
        touchEndX = e.touches[0].clientX;
        touchEndY = e.touches[0].clientY;

        if (fsScaleRef.current > 1 && lastTouch) {
          e.preventDefault();
          const dx = touchEndX - lastTouch.x;
          const dy = touchEndY - lastTouch.y;

          setFsOffset((prev) =>
            calculateBoundedOffset(
              prev.x + dx,
              prev.y + dy,
              fsScaleRef.current,
              fsImgRef.current,
              fsContainer,
            ),
          );
          lastTouch = { x: touchEndX, y: touchEndY };
        } else if (fsScaleRef.current <= 1) {
          const dx = Math.abs(touchStartX - touchEndX);
          const dy = Math.abs(touchStartY - touchEndY);
          if (dx > dy && dx > 10) {
            e.preventDefault();
          }
        }
      }
    };

    const handleFsTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStartDist = null;
      if (e.touches.length === 0) {
        lastTouch = null;

        if (fsScaleRef.current <= 1 && navRefs.current.hasNav) {
          const deltaX = touchStartX - touchEndX;
          const deltaY = touchStartY - touchEndY;
          const swipeThreshold = 50;

          if (
            Math.abs(deltaX) > swipeThreshold &&
            Math.abs(deltaX) > Math.abs(deltaY)
          ) {
            if (deltaX > 0) {
              navRefs.current.handleNext();
            } else {
              navRefs.current.handlePrev();
            }
          }
        }
      }
    };

    const handleFsMouseDown = (e) => {
      if (fsScaleRef.current <= 1 || e.button !== 0) return;
      e.preventDefault();
      isDragging = true;
      lastMouse = { x: e.clientX, y: e.clientY };
    };

    const handleFsMouseMove = (e) => {
      if (!isDragging || fsScaleRef.current <= 1) return;
      e.preventDefault();
      const dx = e.clientX - lastMouse.x;
      const dy = e.clientY - lastMouse.y;

      setFsOffset((prev) =>
        calculateBoundedOffset(
          prev.x + dx,
          prev.y + dy,
          fsScaleRef.current,
          fsImgRef.current,
          fsContainer,
        ),
      );
      lastMouse = { x: e.clientX, y: e.clientY };
    };

    const handleFsMouseUp = () => {
      isDragging = false;
      lastMouse = null;
    };

    fsContainer.addEventListener("wheel", handleFsWheel, { passive: false });
    fsContainer.addEventListener("touchstart", handleFsTouchStart, {
      passive: false,
    });
    fsContainer.addEventListener("touchmove", handleFsTouchMove, {
      passive: false,
    });
    fsContainer.addEventListener("touchend", handleFsTouchEnd);
    fsContainer.addEventListener("touchcancel", handleFsTouchEnd);
    fsContainer.addEventListener("mousedown", handleFsMouseDown);

    window.addEventListener("mousemove", handleFsMouseMove, { passive: false });
    window.addEventListener("mouseup", handleFsMouseUp);

    return () => {
      fsContainer.removeEventListener("wheel", handleFsWheel);
      fsContainer.removeEventListener("touchstart", handleFsTouchStart);
      fsContainer.removeEventListener("touchmove", handleFsTouchMove);
      fsContainer.removeEventListener("touchend", handleFsTouchEnd);
      fsContainer.removeEventListener("touchcancel", handleFsTouchEnd);
      fsContainer.removeEventListener("mousedown", handleFsMouseDown);

      window.removeEventListener("mousemove", handleFsMouseMove);
      window.removeEventListener("mouseup", handleFsMouseUp);

      if (wheelTimeout) clearTimeout(wheelTimeout);
    };
  }, [isFullScreen]);

  // ── Upload handler ──────────────────────────────────────────────────────────
  const handle_submit = async (e) => {
    e.preventDefault();
    const file = fileInput.current.files[0];
    if (!file) return;

    const body_file = {
      file,
      file_name: `img_%${file?.name}_%${uuid()}`,
      dirname: "properties",
    };

    let body_database;
    let is_nested = false;

    if (row?.child_uuid) {
      is_nested = true;
      body_database = {
        geo_layer_id: layer_id,
        feature_key: feature_key_nested_table,
        child_uuid: row.child_uuid,
        properties_key: field?.key,
        properties_value: "",
      };
    } else {
      body_database = {
        geo_layer_id: layer_id,
        feature_key: row?.key,
        properties_key: field?.key,
        properties_value: "",
      };
    }

    dispatch(
      upload_image_table({
        body_file,
        body_database,
        mode: "image",
        is_nested,
      }),
    );
    onClose();
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="box-body"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "5px",
        gap: "5px",
        userSelect: "none",
      }}
    >
      <div
        ref={viewportRef}
        className="no-drag"
        style={{
          position: "relative",
          overflow: "hidden",
          border: "1px solid #ddd",
          background: "#f5f5f5",
          maxHeight: "calc(100svh - 255px)",
          minHeight: currentHeight,
          transition: "min-height 0.3s ease-in-out",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: scale > 1 ? "grab" : "default",
          touchAction: "none",
        }}
      >
        {imageSrc ? (
          <img
            ref={imgRef}
            key={imageSrc}
            src={imageSrc}
            alt="editor preview"
            draggable={false}
            onLoad={handleImageLoad}
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "calc(100svh - 255px)",
              objectFit: "contain",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: "center center",
              opacity: isImgLoading ? 0 : 1,
              transition:
                scale === 1
                  ? "opacity 0.3s ease, transform 0.2s ease-out"
                  : "opacity 0.3s ease",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div
            style={{
              padding: "20px",
              color: "#999",
              fontSize: "13px",
              textAlign: "center",
            }}
            className="container background_white"
          >
            {dict?.["No image"]?.[language] || "No image uploaded yet"}
          </div>
        )}

        {/* ── IMAGE NAVIGATION (Left/Right overlay) ── */}
        {hasNav && (
          <>
            <button
              onClick={handlePrev}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ ...nav_btn_style, left: "10px" }}
              title="Previous image"
            >
              &#10094;
            </button>
            <button
              onClick={handleNext}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{ ...nav_btn_style, right: "10px" }}
              title="Next image"
            >
              &#10095;
            </button>

            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                fontSize: "10px",
                padding: "4px 8px",
                borderRadius: "12px",
                zIndex: 10,
              }}
            >
              {currentIndex + 1} / {totalImages}
            </div>
          </>
        )}

        {/* ── OVERLAY CONTROLS (top-right) ── */}
        {imageSrc && (
          <div
            style={{
              position: "absolute",
              top: "6px",
              right: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "2px",
              zIndex: 10,
            }}
          >
            {/* Full Screen Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(true);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Full Screen"
              style={{ ...zoom_btn_style, marginBottom: "4px" }}
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
              </svg>
            </button>

            {/* Zoom Controls */}
            <button
              onClick={zoom_in}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Zoom in"
              style={zoom_btn_style}
            >
              +
            </button>
            <button
              onClick={zoom_reset}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Reset zoom"
              style={{ ...zoom_btn_style, fontSize: "9px", letterSpacing: 0 }}
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              onClick={zoom_out}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Zoom out"
              disabled={scale <= ZOOM_MIN}
              style={{
                ...zoom_btn_style,
                opacity: scale <= ZOOM_MIN ? 0.35 : 1,
              }}
            >
              −
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          flexShrink: 0,
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        {mode_map === "editor" && (
          <label
            className="button background_black"
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              margin: 0,
            }}
          >
            <img
              src={icon_upload}
              style={{ width: "13px", height: "13px", filter: "invert(1)" }}
              alt="upload"
            />
            {dict?.["Upload New Image"]?.[language] || "Upload New Image"}
            <input
              style={{ display: "none" }}
              type="file"
              accept="image/*"
              ref={fileInput}
              onChange={handle_submit}
            />
          </label>
        )}

        {imageSrc && (
          <a
            href={imageSrc}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="button background_black"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              margin: 0,
            }}
          >
            {dict?.["Download"]?.[language] || "Download"}
          </a>
        )}
      </div>

      {/* ── FULL SCREEN MODAL ── */}
      {isFullScreen && (
        <div
          onClick={() => setIsFullScreen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100svh",
            backgroundColor: "#000000",
            zIndex: 99999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "auto",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              right: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              zIndex: 10,
            }}
          >
            <div
              style={{
                color: "white",
                fontSize: "14px",
                fontFamily: "sans-serif",
                background: "rgba(0,0,0,0.5)",
                padding: "6px 12px",
                borderRadius: "15px",
                opacity: hasNav ? 1 : 0,
              }}
            >
              {currentIndex + 1} / {totalImages}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsFullScreen(false);
              }}
              style={{ ...fs_btn_style, fontSize: "24px", lineHeight: "1" }}
              title="Close"
            >
              &times;
            </button>
          </div>

          {hasNav && (
            <button
              onClick={handlePrev}
              style={{
                ...fs_btn_style,
                position: "absolute",
                left: "20px",
                zIndex: 10,
              }}
              title="Previous"
            >
              &#10094;
            </button>
          )}

          <div
            ref={fsContainerRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              touchAction: "none",
            }}
          >
            {imageSrc ? (
              <img
                ref={fsImgRef}
                src={imageSrc}
                draggable={false}
                alt="Fullscreen Preview"
                style={{
                  maxWidth: "100vw",
                  maxHeight: "100svh",
                  transform: `translate(${fsOffset.x}px, ${fsOffset.y}px) scale(${fsScale})`,
                  transition:
                    fsScale === 1 ? "transform 0.2s ease-out" : "none",
                  objectFit: "contain",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                  cursor: fsScale > 1 ? "grab" : "default",
                }}
              />
            ) : (
              <div
                style={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "16px",
                  fontFamily: "sans-serif",
                  padding: "20px",
                  background: "rgba(20, 20, 20, 0.7)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {dict?.["No image"]?.[language] || "No image uploaded yet"}
              </div>
            )}
          </div>

          {hasNav && (
            <button
              onClick={handleNext}
              style={{
                ...fs_btn_style,
                position: "absolute",
                right: "20px",
                zIndex: 10,
              }}
              title="Next"
            >
              &#10095;
            </button>
          )}

          {imageSrc && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                bottom: "30px",
                display: "flex",
                gap: "10px",
                background: "rgba(20, 20, 20, 0.7)",
                padding: "8px 16px",
                borderRadius: "30px",
                border: "1px solid rgba(255,255,255,0.1)",
                zIndex: 10,
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFsScale((prev) => Math.max(prev - 0.5, ZOOM_MIN));
                }}
                disabled={fsScale <= ZOOM_MIN}
                style={{
                  ...fs_btn_style,
                  width: "36px",
                  height: "36px",
                  opacity: fsScale <= ZOOM_MIN ? 0.4 : 1,
                }}
              >
                -
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFsScale(1);
                  setFsOffset({ x: 0, y: 0 });
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                  padding: "0 10px",
                  fontFamily: "sans-serif",
                }}
              >
                {Math.round(fsScale * 100)}%
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFsScale((prev) => Math.min(prev + 0.5, ZOOM_MAX));
                }}
                disabled={fsScale >= ZOOM_MAX}
                style={{
                  ...fs_btn_style,
                  width: "36px",
                  height: "36px",
                  opacity: fsScale >= ZOOM_MAX ? 0.4 : 1,
                }}
              >
                +
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared Styles ─────────────────────────────────────────────────────────────
const zoom_btn_style = {
  width: "26px",
  height: "26px",
  border: "none",
  borderRadius: "4px",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  fontSize: "16px",
  lineHeight: 1,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const nav_btn_style = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: "32px",
  height: "32px",
  border: "none",
  borderRadius: "50%",
  background: "rgba(0,0,0,0.55)",
  color: "#fff",
  fontSize: "14px",
  lineHeight: 1,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  zIndex: 10,
};

const fs_btn_style = {
  background: "rgba(30, 30, 30, 0.7)",
  border: "1px solid rgba(255,255,255,0.2)",
  color: "white",
  cursor: "pointer",
  borderRadius: "50%",
  width: "44px",
  height: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  transition: "background 0.2s, opacity 0.2s",
};
